import { initReactI18next } from 'react-i18next';
import { MemoryRouterProps } from 'react-router-dom';
import i18n from 'i18next';
import { StyledEngineProvider } from '@mui/material/styles';
import { LicenseInfo } from '@mui/x-license';
import CssBaseline from '@mui/material/CssBaseline';

import fr from '@bloomays-lib/utils.shared';
import { Theming, GlobalErrorBoundary, LoaderGlobal } from '@bloomays-lib/ui.shared';
import type { Auth0Context, Env } from '@bloomays-lib/types.shared';
import { Auth0ProviderConfig, AuthApolloProvider } from '@bloomays-lib/common.auth/browser';

import './app.css';
import { ToastContainer } from '@bloomays-lib/ui.shared';
import { NotificationProvider } from '../contexts/NotificationsProvider';
import { UserProvider } from '../contexts/UserProvider';
import { ErrorBoundary } from '../components/organisms/ErrorBoundary';
import { CRMVisitorLoader } from '../auth/CRMVisitorProvider';
import { Logger } from '../services/serviceLogger';
import RouterWrapper from '../wrappers/RouterWrapper';
import { getApolloConfig } from '../services/apolloConfig';

const logger = Logger('FormBloomers App');

LicenseInfo.setLicenseKey(
  'd2c4d17be9526eea0b78937679baa0d7Tz0xMjMyNDAsRT0xNzk3NDY1NTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y',
);

// eslint-disable-next-line @typescript-eslint/no-floating-promises
i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    resources: {
      fr,
    },
    lng: 'fr', // if you're using a language detector, do not define the lng option
    fallbackLng: 'fr',

    interpolation: {
      escapeValue: false,
    },
  });

type AppProps = {
  router?: MemoryRouterProps;
  auth0ProviderConfig: Auth0ProviderConfig;
  mockAuth0Context?: Auth0Context;
  env: Env;
};

const apolloStoreConfig = getApolloConfig(logger);

const App = ({ router, auth0ProviderConfig, mockAuth0Context, env }: AppProps) => {
  return (
    <Theming>
      <CssBaseline />
      <StyledEngineProvider injectFirst>
        <GlobalErrorBoundary>
          <AuthApolloProvider
            auth0ProviderConfig={auth0ProviderConfig}
            mockAuth0Context={mockAuth0Context}
            logger={logger}
            loaderAuthComponent={<LoaderGlobal waitingText="Chargement jeton utilisateur" />}
            env={env}
            apolloStoreConfig={apolloStoreConfig}
          >
            <ErrorBoundary>
              <UserProvider>
                <CRMVisitorLoader />
                <NotificationProvider>
                  <RouterWrapper injectedRouter={router}>
                    <ToastContainer stacked={true} />
                  </RouterWrapper>
                </NotificationProvider>
              </UserProvider>
            </ErrorBoundary>
          </AuthApolloProvider>
        </GlobalErrorBoundary>
      </StyledEngineProvider>
    </Theming>
  );
};

export default App;
